import { forwardRef, useImperativeHandle } from "react";

import { WarningConfirmationModal } from "../../components";
import { CurrencyFormat } from "../../components/textFieldCurrency/CurrencyUtils";
import { isEmpty } from "../../helpers/utils";

import { BillingTypeConfigureTableWrapper } from "./BillingTypeConfigureTable.style";
import { BillingConfigurationMilestoneType } from "./billingConfigurationMilestoneType/BillingConfigurationMilestoneType";
import { BillingConfigurationScheduleType } from "./billingConfigurationScheduleType/BillingConfigurationScheduleType";

import { getDeleteMessageBasedOnBillingType } from "./helper";
import { BillingConfigurationDetailsFormKeyProps } from "./modelsAndSchemas/Models";
import { useBillingTypeConfigureTableHook } from "./useBillingTypeConfigureTableHook";

export interface BillingTypeConfigureTableProps {
  pageId: string;
  currencyName: string;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  forceUpdate: () => void;
  programTemplateId: string;
  isFromProject?: boolean;
  isFromBillingInvoice?: boolean;
  isInEditMode?: boolean;
  billingConfigurationDetailsFormKeys: BillingConfigurationDetailsFormKeyProps;
  clientId: string;
  isViewOnly?: boolean;
}

export const BillingTypeConfigureTable = forwardRef(
  (props: BillingTypeConfigureTableProps, ref) => {
    const {
      pageId,
      currencyName,
      currencyFormat,
      currencyId,
      forceUpdate,
      programTemplateId,
      isFromProject = false,
      isFromBillingInvoice,
      billingConfigurationDetailsFormKeys,
      isViewOnly = false,
    } = props;

    const {
      handleScheduleOrMilestoneConfigurationAction,
      isBillingTypeScheduleBase,
      isBillingTypeMilestoneBase,
      isSchedulePeriodMonthly,
      filteredResults,
      onDataAddedCallback,
      onDataDeletedCallback,
      showDeleteScheduleOrMilestoneModal,
      setSelectedEntryForEdit,
      addBillingConfigurationScheduleOrMilestone,
      updateBillingConfigurationScheduleOrMilestone,
      clearAllValues,
      onCancelEditFlow,
      handleModalClose,
      handleDeleteScheduleOrMilestoneClick,
      checkIfFeeGiven,
      commonLabels,
      topicsList,
      areYouSureYouWantToDeleteTheSchedule,
      areYouSureYouWantToDeleteTheMilestone,
    } = useBillingTypeConfigureTableHook({ ...props });

    useImperativeHandle(ref, () => ({
      resetData,
    }));

    const resetData = () => {
      clearAllValues();
    };

    if (isFromProject === false && isEmpty(programTemplateId)) return null;

    const commonProps = {
      pageId,
      filteredResults,
      onFocusAmountHandled: checkIfFeeGiven,
      currencyFormat,
      currencyId,
      currencyName,
      addBillingConfigurationSchedule:
        addBillingConfigurationScheduleOrMilestone,
      onDataAddedCallback,
      onDataDeletedCallback,
      onEditCancel: onCancelEditFlow,
      billingConfigurationDetailsFormKeys,
      forceUpdate,
      isFromBillingInvoice,
      isViewOnly,
    };

    return (
      <>
        <BillingTypeConfigureTableWrapper data-testid={"configure-table"}>
          {isBillingTypeScheduleBase() && (
            <BillingConfigurationScheduleType
              {...commonProps}
              isSchedulePeriodMonthly={isSchedulePeriodMonthly()}
              onDeleteBillingConfigurationScheduleTableEntryAction={
                handleScheduleOrMilestoneConfigurationAction
              }
              updateBillingConfigurationSchedule={
                updateBillingConfigurationScheduleOrMilestone
              }
              onEditScheduleEntry={setSelectedEntryForEdit}
            />
          )}
          {isBillingTypeMilestoneBase() && (
            <BillingConfigurationMilestoneType
              {...commonProps}
              topicsList={topicsList}
              onDeleteBillingConfigurationMilestoneTableEntryAction={
                handleScheduleOrMilestoneConfigurationAction
              }
              updateBillingConfigurationMilestone={
                updateBillingConfigurationScheduleOrMilestone
              }
              onEditMilestoneEntry={setSelectedEntryForEdit}
              isFromBillingInvoice={isFromBillingInvoice}
            />
          )}
        </BillingTypeConfigureTableWrapper>
        {showDeleteScheduleOrMilestoneModal && (
          <WarningConfirmationModal
            show={showDeleteScheduleOrMilestoneModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleModalClose}
            secondaryButtonTitle={commonLabels.no}
            onPrimaryButtonClick={handleDeleteScheduleOrMilestoneClick}
            labelContent={getDeleteMessageBasedOnBillingType(
              isBillingTypeScheduleBase(),
              areYouSureYouWantToDeleteTheSchedule,
              areYouSureYouWantToDeleteTheMilestone,
            )}
          />
        )}
      </>
    );
  },
);
